import React, { useState, useEffect, useCallback } from 'react';
import { Car, Route, ChevronDown, ChevronUp } from 'lucide-react';
import PlacesAutocompleteInput from '../../components/PlacesAutocompleteInput';
import CompleteRouteDetails from '../../components/VehicleDetails/CompleteRouteDetails';
import config from '../../config';
import { toast } from 'react-toastify';
import './VehicleDetails.css';

const BODY_TYPE_OPTIONS = ['Open', 'Closed', 'Container'];

function selectedBodyType(value) {
  const raw = String(value || '').trim();
  const match = BODY_TYPE_OPTIONS.find((option) => option.toLowerCase() === raw.toLowerCase());
  return match || raw;
}

const getVehicleColor = (vehicleType, isTransporter = false) => {
  if (isTransporter) return '#f472b6';
  const colors = {
    class1: '#ef4444',
    class2: '#3b82f6',
    class3: '#10b981',
    class4: '#f59e0b',
    class5: '#8b5cf6',
    class6: '#06b6d4',
    class7: '#f97316',
    class8: '#84cc16',
    class9: '#ec4899',
  };
  return colors[vehicleType] || '#10b981';
};

const VehicleDetailsModal = ({
  isOpen = false,
  vehicleId = null,
  onClose = () => {},
  onVehicleUpdated = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [registeredOwner, setRegisteredOwner] = useState(null);
  const [ownerFormOpen, setOwnerFormOpen] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ name: '', phone: '' });
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [detailRoutesExpanded, setDetailRoutesExpanded] = useState(false);
  const [bookingsTotalPay, setBookingsTotalPay] = useState(null);

  const fetchVehicleDetails = useCallback(async () => {
    if (!vehicleId || !isOpen) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${config.opsApiBase}/vehicles/${vehicleId}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to load vehicle (status ${res.status})`);
      }
      const data = await res.json();
      setVehicle(data);

      const driver = data.driver || {};
      const ownerData = data.owner && data.owner.user_id ? data.owner : null;
      setRegisteredOwner(ownerData);
      setOwnerForm({ name: ownerData?.name || '', phone: ownerData?.phone || '' });
      setOwnerFormOpen(false);

      const currentVehicleName = data.make && data.model ? `${data.make} ${data.model}` : '';
      setEditFormData({
        registration_no: data.registration_no || `REG-${data.id || vehicleId}`,
        vehicle_name: currentVehicleName,
        make: data.make || '',
        model: data.model || '',
        class: data.class_ ? String(data.class_) : '1',
        body_type: data.body_type || '',
        specialization: data.specialization || '',
        tonnage: data.tonnage != null ? String(data.tonnage) : '',
        length_ft: data.length_ft != null ? String(data.length_ft) : (data.length != null ? String(data.length) : ''),
        breadth_ft: data.breadth_ft != null ? String(data.breadth_ft) : (data.breadth != null ? String(data.breadth) : ''),
        height_ft: data.height_ft != null ? String(data.height_ft) : (data.height != null ? String(data.height) : ''),
        status: data.status || 'idle',
        is_active: data.is_active !== undefined ? data.is_active : true,
        is_blocked: !!data.is_blocked,
        is_part: !!data.is_part,
        is_transporter: !!data.is_transporter,
        driver_name: driver.name || '',
        driver_phone: driver.phone || '',
        driver_rating: driver.rating ? String(driver.rating) : '',
        language_preference: driver.language_preference || '',
        remarks: data.remarks || '',
        joining_bonus_paid: !!driver.joining_bonus_paid,
        base_location: data.base_location || '',
        base_lat: data.base_lat || '',
        base_lng: data.base_lng || '',
        resolved_address: data.base_location || '',
        phone_extensions: Array.isArray(data.phone_extensions) ? data.phone_extensions : [],
        transport_routes: Array.isArray(data.transport_routes) ? data.transport_routes : [],
      });
    } catch (err) {
      console.error('Failed to fetch vehicle:', err);
      toast.error(err.message || 'Failed to load vehicle details');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, isOpen]);

  useEffect(() => {
    if (isOpen && vehicleId) {
      setIsEditing(false);
      fetchVehicleDetails();
    } else {
      setVehicle(null);
      setEditFormData({});
    }
  }, [isOpen, vehicleId, fetchVehicleDetails]);

  if (!isOpen) return null;

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleToggleActiveState = async () => {
    const newActiveState = !editFormData.is_active;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}/state`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: newActiveState ? 'active' : 'inactive' }),
      });
      if (res.ok) {
        setEditFormData((prev) => ({ ...prev, is_active: newActiveState }));
        setVehicle((prev) => (prev ? { ...prev, is_active: newActiveState } : prev));
        toast.success(`Vehicle ${newActiveState ? 'activated' : 'deactivated'}`);
      } else {
        toast.error('Failed to update vehicle state');
      }
    } catch (err) {
      toast.error('Network error updating state');
    }
  };

  const handleToggleBlockedState = async () => {
    const newBlockedState = !editFormData.is_blocked;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}/block`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_blocked: newBlockedState }),
      });
      if (res.ok) {
        setEditFormData((prev) => ({ ...prev, is_blocked: newBlockedState }));
        setVehicle((prev) => (prev ? { ...prev, is_blocked: newBlockedState } : prev));
        toast.success(`Vehicle ${newBlockedState ? 'blocked' : 'unblocked'}`);
      } else {
        toast.error('Failed to update blocked state');
      }
    } catch (err) {
      toast.error('Network error updating blocked state');
    }
  };

  const handleTogglePartBooking = async () => {
    const newValue = !editFormData.is_part;
    if (window.confirm(`Are you sure you want to ${newValue ? 'ACTIVATE' : 'DEACTIVATE'} Part Booking mode?`)) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}/part-booking?is_part=${newValue}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setEditFormData((prev) => ({ ...prev, is_part: newValue }));
          setVehicle((prev) => (prev ? { ...prev, is_part: newValue } : prev));
          toast.success(`Part booking ${newValue ? 'enabled' : 'disabled'}`);
        } else {
          toast.error('Failed to toggle part booking');
        }
      } catch (err) {
        toast.error('Network error updating part booking');
      }
    }
  };

  const handleCheckMap = () => {
    const addr = editFormData.base_location || editFormData.resolved_address || '';
    const lat = parseFloat(editFormData.base_lat);
    const lng = parseFloat(editFormData.base_lng);
    let query = '';
    if (lat && lng) query = `${lat},${lng}`;
    else if (addr.trim()) query = encodeURIComponent(addr.trim());
    if (query) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    } else {
      toast.info('No location coordinates or address available');
    }
  };

  const handleSaveVehicle = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        registration_no: editFormData.registration_no,
        make: editFormData.make,
        model: editFormData.model,
        class_: parseInt(editFormData.class, 10) || 1,
        body_type: editFormData.body_type || null,
        specialization: editFormData.specialization || null,
        tonnage: parseFloat(editFormData.tonnage) || null,
        length: parseFloat(editFormData.length_ft) || null,
        breadth: parseFloat(editFormData.breadth_ft) || null,
        height: parseFloat(editFormData.height_ft) || null,
        driver_name: editFormData.driver_name,
        driver_phone: editFormData.driver_phone,
        driver_rating: editFormData.driver_rating ? parseFloat(editFormData.driver_rating) : null,
        language_preference: editFormData.language_preference,
        remarks: editFormData.remarks,
        base_location: editFormData.base_location,
        base_lat: editFormData.base_lat,
        base_lng: editFormData.base_lng,
        phone_extensions: editFormData.phone_extensions,
        transport_routes: editFormData.transport_routes,
      };

      const res = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error('Failed to save vehicle details');
      }
      toast.success('Vehicle updated successfully');
      setIsEditing(false);
      fetchVehicleDetails();
      if (onVehicleUpdated) onVehicleUpdated();
    } catch (err) {
      toast.error(err.message || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOwner = async () => {
    if (!ownerForm.name.trim() || !ownerForm.phone.trim()) {
      toast.error('Owner name and phone are required');
      return;
    }
    setOwnerSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}/owner`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ownerForm),
      });
      if (res.ok) {
        toast.success('Owner updated successfully');
        setOwnerFormOpen(false);
        fetchVehicleDetails();
      } else {
        toast.error('Failed to update owner');
      }
    } catch (err) {
      toast.error('Network error saving owner');
    } finally {
      setOwnerSaving(false);
    }
  };

  const activeKm = vehicle?.total_active_km || 0;
  const deadKm = vehicle?.total_dead_km || 0;
  const totalPay = bookingsTotalPay !== null ? bookingsTotalPay : (vehicle?.total_pay || 0);
  const baseLoc = editFormData.base_location || vehicle?.base_location || '—';

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 2000 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content modal-content-wide"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '1200px', width: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Car size={24} style={{ color: getVehicleColor(vehicle?.vehicle_type, vehicle?.is_transporter) }} />
            <h2>Vehicle #{vehicle?.id || vehicleId}</h2>
            <button
              className={`btn-edit-toggle ${isEditing ? 'editing' : ''}`}
              onClick={handleEditToggle}
              style={{ marginLeft: '12px', flexShrink: 0 }}
            >
              {isEditing ? '✕ Cancel' : '✏️ Edit'}
            </button>
          </div>

          <div className="modal-header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn-assign-booking-header"
              onClick={() => {
                window.location.href = `#/app/bookings?assignVehicle=${vehicleId}`;
              }}
            >
              Assign Booking
            </button>
            <button
              className="btn-bonus-pay-header"
              onClick={() => toast.info('Bonus Pay feature available from dispatch page')}
              title="Add bonus payment for driver"
            >
              Bonus Pay
            </button>
            <button
              className="btn-part-booking-header"
              onClick={handleTogglePartBooking}
              title={editFormData.is_part ? 'Vehicle is in Part Booking mode' : 'Enable Part Booking mode'}
              style={{
                backgroundColor: editFormData.is_part ? '#6f42c1' : '#f8f9fa',
                color: editFormData.is_part ? 'white' : '#495057',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {editFormData.is_part ? 'Part Booking ✓' : 'Make Part Booking'}
            </button>
            <button
              className="btn-bonus-history-header"
              onClick={() => toast.info('Bonus history available from vehicles page')}
            >
              Bonus History
            </button>
            <button className="modal-close-btn" onClick={onClose} style={{ fontSize: 24, cursor: 'pointer' }}>
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: 28, marginBottom: 10 }} />
            <div>Loading vehicle details...</div>
          </div>
        ) : (
          <div className="modal-body-split" style={{ overflowY: 'auto', flex: 1 }}>
            {/* Left Half - Vehicle & Driver Info */}
            <div className="modal-left-half">
              {/* Vehicle Information */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                    Vehicle Information
                  </h3>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      className={`btn-state-toggle ${editFormData.is_active ? 'active' : 'inactive'}`}
                      onClick={handleToggleActiveState}
                      title={editFormData.is_active ? 'Click to deactivate' : 'Click to activate'}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer',
                        border: 'none',
                        background: editFormData.is_active ? '#dcfce7' : '#fee2e2',
                        color: editFormData.is_active ? '#15803d' : '#b91c1c',
                      }}
                    >
                      {editFormData.is_active ? '🟢 Active' : '🔴 Inactive'}
                    </button>

                    <button
                      type="button"
                      onClick={handleToggleBlockedState}
                      style={{
                        backgroundColor: editFormData.is_blocked ? '#f59e0b' : '#fef3c7',
                        color: editFormData.is_blocked ? '#fff' : '#92400e',
                        border: '1px solid #fbbf24',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      {editFormData.is_blocked ? '🚫 Blocked' : '✓ Not blocked'}
                    </button>

                    <button
                      type="button"
                      onClick={() => toast.info('Vehicle documents viewer')}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      📁 Vehicle Docs
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this vehicle?')) {
                          toast.warning('Deletion restricted to dispatch vehicles page');
                        }
                      }}
                      style={{
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      🗑️ Delete Vehicle
                    </button>
                  </div>
                </div>
              </div>

              {/* Vehicle Owner */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                    Vehicle Owner
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      if (!ownerFormOpen) {
                        setOwnerForm({
                          name: registeredOwner?.name || '',
                          phone: registeredOwner?.phone || '',
                        });
                      }
                      setOwnerFormOpen(!ownerFormOpen);
                    }}
                    style={{
                      padding: '6px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    {ownerFormOpen ? 'Cancel' : registeredOwner ? 'Change Owner' : 'Add Owner'}
                  </button>
                </div>

                {ownerFormOpen ? (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                          Owner Name *
                        </label>
                        <input
                          type="text"
                          placeholder="Full name"
                          value={ownerForm.name}
                          onChange={(e) => setOwnerForm((prev) => ({ ...prev, name: e.target.value }))}
                          className="form-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                          Phone Number *
                        </label>
                        <input
                          type="text"
                          placeholder="10 digits phone"
                          value={ownerForm.phone}
                          onChange={(e) => setOwnerForm((prev) => ({ ...prev, phone: e.target.value }))}
                          className="form-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveOwner}
                      disabled={ownerSaving}
                      style={{
                        padding: '7px 16px',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      {ownerSaving ? 'Saving...' : 'Save Owner'}
                    </button>
                  </div>
                ) : registeredOwner ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                    <div>
                      <span style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Owner Name</span>
                      <input type="text" value={registeredOwner.name || '—'} readOnly className="form-input readonly" style={{ width: '100%' }} />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Phone Number</span>
                      <input type="text" value={registeredOwner.phone || '—'} readOnly className="form-input readonly" style={{ width: '100%' }} />
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                    No owner registered for this vehicle.
                  </div>
                )}
              </div>

              {/* Base Location */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                    Base Location
                  </h3>
                  <button
                    type="button"
                    onClick={handleCheckMap}
                    style={{
                      padding: '6px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      backgroundColor: '#10b981',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    🗺️ Check Map
                  </button>
                </div>

                {isEditing ? (
                  <PlacesAutocompleteInput
                    value={editFormData.base_location || ''}
                    onChange={(text) => setEditFormData((prev) => ({ ...prev, base_location: text }))}
                    onPlaceSelect={(place) => {
                      setEditFormData((prev) => ({
                        ...prev,
                        base_location: place.address,
                        base_lat: place.lat,
                        base_lng: place.lng,
                        resolved_address: place.address,
                      }));
                    }}
                    placeholder="Search base location..."
                  />
                ) : (
                  <input
                    type="text"
                    value={baseLoc}
                    readOnly
                    className="form-input readonly"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                )}
              </div>

              {/* Driver Information Card */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                  Driver Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Registration Number *</label>
                    <input
                      type="text"
                      name="registration_no"
                      value={editFormData.registration_no || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Make</label>
                    <input
                      type="text"
                      name="make"
                      value={editFormData.make || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Model</label>
                    <input
                      type="text"
                      name="model"
                      value={editFormData.model || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Driver Name</label>
                    <input
                      type="text"
                      name="driver_name"
                      value={editFormData.driver_name || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Phone Number</label>
                    <input
                      type="tel"
                      name="driver_phone"
                      value={editFormData.driver_phone || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Language Preference</label>
                    <input
                      type="text"
                      name="language_preference"
                      value={editFormData.language_preference || 'Not specified'}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Driver Rating</label>
                    <input
                      type="text"
                      name="driver_rating"
                      value={editFormData.driver_rating ? `${editFormData.driver_rating} ★` : 'Not rated'}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Joining Bonus</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={editFormData.joining_bonus_paid ? 'Paid' : 'Not Paid'}
                        readOnly
                        className="form-input readonly"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Remarks</label>
                    <textarea
                      name="remarks"
                      value={editFormData.remarks || 'No remarks added'}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%', minHeight: 60, resize: 'vertical' }}
                    />
                  </div>
                </div>

              </div>

              {/* Vehicle Details — same block that sits under Driver Information on the vehicles page */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                  Vehicle Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  {[
                    ['make', 'Make', 'text'],
                    ['model', 'Model', 'text'],
                    ['length_ft', 'Length', 'text'],
                    ['breadth_ft', 'Breadth', 'text'],
                    ['height_ft', 'Height', 'text'],
                    ['tonnage', 'Tonnage (ton)', 'number'],
                  ].map(([name, label, type]) => (
                    <div className="form-group" key={name}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>{label}</label>
                      <input
                        type={type}
                        step={type === 'number' ? '0.01' : undefined}
                        name={name}
                        value={editFormData[name] || ''}
                        onChange={handleFormChange}
                        disabled={!isEditing}
                        className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  ))}

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Body Type</label>
                    <select
                      name="body_type"
                      value={selectedBodyType(editFormData.body_type)}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    >
                      <option value="">Select body type</option>
                      {selectedBodyType(editFormData.body_type)
                        && !BODY_TYPE_OPTIONS.includes(selectedBodyType(editFormData.body_type)) && (
                        <option value={selectedBodyType(editFormData.body_type)}>
                          {selectedBodyType(editFormData.body_type)}
                        </option>
                      )}
                      {BODY_TYPE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Vehicle Class</label>
                    <input
                      type="number"
                      name="class"
                      min="1"
                      value={editFormData.class || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Specialization</label>
                    <select
                      name="specialization"
                      value={editFormData.specialization || ''}
                      onChange={handleFormChange}
                      disabled={!isEditing}
                      className={`form-input ${!isEditing ? 'readonly' : ''}`}
                      style={{ width: '100%' }}
                    >
                      <option value="">None</option>
                      <option value="Tipper">Tipper</option>
                      <option value="Refrigerated">Refrigerated</option>
                    </select>
                  </div>
                </div>

                {isEditing && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #d1d5db',
                        background: '#fff',
                        borderRadius: 6,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveVehicle}
                      disabled={saving}
                      style={{
                        padding: '8px 18px',
                        background: '#2563eb',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontWeight: 600,
                        cursor: saving ? 'wait' : 'pointer',
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Vehicle'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Half - KM Stats & Route Bookings */}
            <div className="modal-right-half">
              {/* Total Distance Statistics */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div
                  style={{
                    backgroundColor: '#e8f5e9',
                    padding: '16px 20px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid #c8e6c9',
                  }}
                >
                  <div style={{ fontSize: '13px', color: '#2e7d32', fontWeight: '600', marginBottom: '4px' }}>
                    💰 Total Pay
                  </div>
                  <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#1b5e20' }}>
                    ₹{Number(totalPay).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Complete Route Details component */}
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 16 }}>
                <CompleteRouteDetails
                  vehicleId={vehicle?.id || vehicleId}
                  onTotalPayCalculated={setBookingsTotalPay}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleDetailsModal;
